import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { scanError, useStartScan } from './site.js';
import { Button } from './ui.js';

/**
 * "Check the whole site again" (owner's request, 25.09): availability, SSL, domain, DNS, e-mail, the
 * home page, speed and – once ownership is confirmed – all pages and links, right now. Counts as one
 * manual scan of the plan's daily limit.
 */
export function FullRescan({
  siteId,
  panel,
}: {
  siteId: string;
  /**
   * The dashboard's score card (owner's mockup A, 25.09): a large refresh icon centred above the
   * button, bottom right, with `panel.aside` (the export) to its left and the last check below.
   */
  panel?: { aside: ReactNode; lastCheck: string | null };
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const scan = useStartScan(siteId, 'full');
  const result = scan.data;
  const start = () =>
    scan.mutate(undefined, {
      // Most checks finish within a minute; the crawl and speed test take a few more.
      onSuccess: () =>
        setTimeout(() => void client.invalidateQueries({ queryKey: ['site', siteId] }), 30_000),
    });
  const messages = (
    <>
      {result ? (
        <p role="status" className="text-sm">
          {result.crawl === false ? (
            <>
              {t('rescan.queuedNoCrawl')}{' '}
              <Link to={`/sites/${siteId}/settings`} className="text-blue-800 underline">
                {t('rescan.verify')}
              </Link>
            </>
          ) : (
            t('rescan.queued')
          )}
        </p>
      ) : null}
      {scan.isError ? (
        <p role="alert" className="text-sm text-red-800">
          {scanError(scan.error, t)}
        </p>
      ) : null}
    </>
  );

  if (panel)
    return (
      <div className="flex flex-col gap-2 sm:max-w-sm sm:items-end sm:text-right">
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-end">
          {panel.aside}
          <button
            type="button"
            title={t('rescan.hint')}
            disabled={scan.isPending || scan.isSuccess}
            onClick={start}
            className="group flex flex-col items-center gap-2 rounded-lg focus-visible:outline-offset-4 disabled:cursor-not-allowed"
          >
            <span
              aria-hidden="true"
              className="grid h-18 w-18 place-items-center rounded-full bg-blue-50 text-blue-700 group-hover:bg-blue-100 group-disabled:opacity-70"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`h-10 w-10 ${scan.isPending ? 'motion-safe:animate-spin' : ''}`}
              >
                <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                <path d="M8 16H3v5" />
              </svg>
            </span>
            <span className="inline-flex min-h-11 w-full items-center justify-center rounded-md bg-blue-700 px-4 font-semibold whitespace-nowrap text-white group-hover:bg-blue-800 group-disabled:bg-blue-400">
              {t('rescan.button')}
            </span>
          </button>
        </div>
        {panel.lastCheck ? (
          <p className="text-center text-sm text-slate-700 sm:text-right">{panel.lastCheck}</p>
        ) : null}
        {messages}
      </div>
    );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          busy={scan.isPending}
          disabled={scan.isSuccess}
          onClick={start}
        >
          {t('rescan.button')}
        </Button>
        {scan.isIdle ? <span className="text-sm text-slate-700">{t('rescan.hint')}</span> : null}
      </div>
      {messages}
    </div>
  );
}
